import type { Notification } from '../../types/notification'
import { api } from './client'

export interface GetNotificationsParams {
  page?: number
  pageSize?: number
  workspaceId?: string
  type?: string
  fromDate?: Date | string
  toDate?: Date | string
  isRead?: boolean
}

export interface GetNotificationsResponse {
  notifications: Notification[]
  pagination: {
    page: number
    pageSize: number
    totalPages: number
    totalCount: number
  }
}

export interface NotificationCountResponse {
  count: number
}

export interface MarkSingleReadResponse {
  notification: Notification
}

export interface BulkReadResponse {
  success: boolean
  count: number
}

export interface MarkAllReadResponse {
  message?: string
  count: number
}

/**
 * Fetch paginated notifications with optional filters
 */
export async function getNotifications(
  params?: GetNotificationsParams,
): Promise<GetNotificationsResponse> {
  const queryParams = new URLSearchParams()

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Map pageSize to limit for backend compatibility
        const paramKey = key === 'pageSize' ? 'limit' : key
        // Convert Date objects to ISO strings for query params
        const paramValue = value instanceof Date ? value.toISOString() : String(value)
        queryParams.append(paramKey, paramValue)
      }
    })
  }

  const response = await api.get(`/notifications${queryParams.toString() ? `?${queryParams}` : ''}`)
  return response.data
}

/**
 * Get count of unread notifications
 */
export async function getUnreadCount(workspaceId?: string): Promise<NotificationCountResponse> {
  const queryParams = workspaceId ? `?workspaceId=${workspaceId}` : ''
  const response = await api.get(`/notifications/unread-count${queryParams}`)
  return response.data
}

/**
 * Mark a single notification as read
 */
export async function markAsRead(notificationId: string): Promise<MarkSingleReadResponse> {
  const response = await api.patch(`/notifications/${notificationId}/read`)
  return response.data
}

/**
 * Mark multiple notifications as read
 */
export async function markMultipleAsRead(notificationIds: string[]): Promise<BulkReadResponse> {
  const response = await api.patch('/notifications/bulk-read', { notificationIds })
  return response.data
}

/**
 * Mark all notifications as read with optional filters
 */
export async function markAllAsRead(filters?: {
  workspaceId?: string
  type?: string
  fromDate?: Date | string
  toDate?: Date | string
}): Promise<MarkAllReadResponse> {
  const response = await api.patch('/notifications/mark-all-read', filters || {})
  return response.data
}

/**
 * Delete (soft delete) a notification
 */
export async function deleteNotification(notificationId: string): Promise<{ success: boolean }> {
  const response = await api.delete(`/notifications/${notificationId}`)
  return response.data
}

/**
 * Delete multiple notifications
 */
export async function deleteMultipleNotifications(
  notificationIds: string[],
): Promise<{ success: boolean, count: number }> {
  const response = await api.delete('/notifications/bulk', { data: { notificationIds } })
  return response.data
}

/**
 * Get user's notification preferences
 */
export async function getNotificationPreferences(): Promise<{
  email: boolean
  sms: boolean
  inApp: boolean
  candidateUpdates: boolean
  referenceProgress: boolean
  referenceCompletions: boolean
  weeklySummary: boolean
  systemAnnouncements: boolean
}> {
  const response = await api.get('/notifications/preferences')
  return response.data
}

/**
 * Update user's notification preferences
 */
export async function updateNotificationPreferences(preferences: {
  email?: boolean
  sms?: boolean
  inApp?: boolean
  candidateUpdates?: boolean
  referenceProgress?: boolean
  referenceCompletions?: boolean
  weeklySummary?: boolean
  systemAnnouncements?: boolean
}): Promise<{
  email: boolean
  sms: boolean
  inApp: boolean
  candidateUpdates: boolean
  referenceProgress: boolean
  referenceCompletions: boolean
  weeklySummary: boolean
  systemAnnouncements: boolean
}> {
  const response = await api.patch('/notifications/preferences', preferences)
  return response.data
}
